/**
 * Shared presentational primitives for the player's self-description forms
 * (issue #214).
 *
 * These four lived inside PlayerIdentity.jsx while it was the only page
 * rendering them. Issue #214 moved the Mental Performance section out of
 * Identity & Vision and onto its own Reflections tab — two pages, one visual
 * language — so they moved here rather than being copied. A copy is how the
 * card chrome, the question label and the save row drift apart between two
 * tabs of the same page.
 *
 * Presentation only: no data fetching, no save path, no knowledge of which
 * section of `player_identity` a caller is editing.
 */
import { useId, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Save, Loader2, ChevronDown } from "lucide-react";
import { assertSectionHeaderState } from "../lib/sectionCardState";
import { track } from "../lib/usageTracker";

// #1394: the r5 fold header's tap box (`tapBox`). Padding raises the button to the 44px tap token over the 2.5rem
// icon tile (nothing when the tile is already 44px or more); the equal negative margin keeps the layout footprint.
const R5_HEADER_PAD = "max(0px, calc((var(--nw-size-tap) - 2.5rem) / 2))";
const R5_HEADER_TAP = { paddingBlock: R5_HEADER_PAD, marginBlock: `calc(-1 * ${R5_HEADER_PAD})` };

/** The icon tile + title + description block, identical open or closed.
 * `className` is appended to the root — the collapsible header uses it for
 * the flex sizing that stops this block being squashed by a wide header
 * state (#528); the heading's own markup never varies. */
function SectionHeading({ icon: Icon, title, desc, className = "" }) {
  return (
    <div className={`flex items-center gap-3 min-w-0 ${className}`.trim()}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
        <Icon size={18} color="#0B2E59" />
      </div>
      <div className="min-w-0">
        <h3 className="font-display font-bold text-lg text-slate-900 break-words">{title}</h3>
        {desc && <p className="text-sm text-slate-500 break-words">{desc}</p>}
      </div>
    </div>
  );
}

function ShippedFrame({ contentPadding, testid, children }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className={`${contentPadding} space-y-4`}>{children}</CardContent>
    </Card>
  );
}

// The kit surface tokens as `pages/GuidePage.jsx` draws them, no shadow, and
// the 10px gap between the frame's children that the approved SettingsPlayer
// and SettingsCoach boards draw (#1135, D259; the shipped CardContent's
// `space-y-4` is 16px).
function KitFrame({ testid, children }) {
  return (
    <div
      className="flex flex-col"
      data-testid={testid}
      style={{
        gap: 10,
        padding: 14,
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      {children}
    </div>
  );
}

/**
 * A section card. Plain and always-open by default — which is now only what
 * the first-run walkthrough asks for (`PlayerIdentity.jsx` renders Player
 * Vision non-collapsible while `embedded`). Every section on a TAB folds
 * since #457/#458.
 *
 * COLLAPSIBLE MODE (issue #378; generalised beyond log sections by
 * #456/#457/#458/#459).
 * Pass `collapsible` and the card folds, with three properties the decision
 * on #378 names explicitly:
 *
 *  - A HEADER STATE renders ON THE HEADER, visible while folded. Andreas: a
 *    fold that does not tell you what is inside is worse than no fold at all,
 *    because the player cannot tell whether there is anything in there. That
 *    obligation is not optional decoration, and it has two shapes because
 *    sections have two kinds of state:
 *
 *      `count`   — LOG-type sections, whose state IS a number of countable
 *                  entries (reflection log, monthly check-ins, season and
 *                  match reflections). MANDATORY for those, and read even
 *                  when it is 0 — an explicit "nothing yet" beats an empty
 *                  header.
 *      `summary` — the SAME obligation for every OTHER kind of section,
 *                  whose state is not a number: "Large" for Text Size, "3
 *                  on" for Notifications, "Complete" for Health and
 *                  emergency contact, "4 of 8" for a form section whose
 *                  questions are part-answered (`sectionFillSummary` in
 *                  `lib/sectionCardState.js` derives that one — Identity &
 *                  Vision and Mental Performance use it). A section with
 *                  genuinely nothing to report (Send Feedback) passes an em
 *                  dash — the analogue of `count={0}`: an explicit nothing,
 *                  not a silent absence. What a summary must never be is a
 *                  CONSTANT: a badge that cannot change is a fold that says
 *                  nothing wearing something that looks like it does.
 *
 *    THE INVARIANT: a collapsible SectionCard carries EXACTLY ONE of `count`
 *    and `summary`. Both is a developer error — two competing answers to one
 *    question — and so is neither, which is precisely the headerless fold
 *    #378 was filed to prevent. Adding `summary` generalises that rule to a
 *    second kind of section; it does not weaken it, and "settings have
 *    nothing to count" is not licence to ship a fold that says nothing.
 *
 *    THE TWO VIOLATIONS ARE PUNISHED DIFFERENTLY, ON PURPOSE. They are not
 *    the same kind of mistake, and a reader who assumes they are will tidy
 *    this back into one branch. Do not.
 *
 *      BOTH    — throws, in every environment including production. The card
 *                cannot render: it has been handed two answers to one
 *                question and no basis for choosing. There is no correct
 *                output to degrade to, so failing loudly is the only honest
 *                thing left. This follows the codebase's one existing
 *                precedent for using a component wrongly (`useCarousel`
 *                outside a `<Carousel>`, `components/ui/carousel.jsx`).
 *      NEITHER — throws in dev and test; in PRODUCTION it renders the fold
 *                with a bare header and does not throw. The card renders
 *                perfectly well here — a guideline is broken, not the
 *                component. This app has no ErrorBoundary anywhere, so a
 *                production throw takes a whole page down over a missing
 *                badge, and takes it down in front of a PLAYER rather than
 *                the developer who left the prop off. That trade is wrong at
 *                any severity of guideline.
 *
 *    Softening the production path costs nothing, because the runtime is not
 *    where this rule is actually enforced. `identityForm.test.js` parses
 *    every `<SectionCard>` call site in the repo and fails if a collapsible
 *    one carries neither prop — so a violation is caught in CI, long before
 *    a bundle exists. The dev/test throw is the fast feedback; that test is
 *    the gate. If you ever delete that test, this decision changes with it.
 *
 *    The check itself is `assertSectionHeaderState` in
 *    `lib/sectionCardState.js` — out of the render body so that it has a
 *    behavioural test, since this repo mounts no components in unit tests.
 *  - `defaultOpen={false}` is the point of the exercise: the log sections
 *    arrive folded so a player with a season of entries meets a page they
 *    can read, not a scroll.
 *  - The trigger carries `aria-expanded` + `aria-controls` and its own
 *    `toggleTestid`. Both matter: the ARIA pair is how a screen reader is
 *    told this is a disclosure, and the testid is what the responsive
 *    harness needs in `TAB_EXPAND_TRIGGERS` (frontend/e2e/dialog-config.js)
 *    to open the fold before measuring. A fold the harness cannot open is
 *    a fold whose contents are never checked at any viewport — the exact
 *    blind spot that hid /learn?tab=training for weeks (#124/#125).
 *    The header state carries a testid of its own on the same stem —
 *    `${toggleTestid}-count` or `${toggleTestid}-summary` — so a test can
 *    assert WHICH kind of state a card is showing, not merely that it shows
 *    one.
 *
 * The body CONTENT is unmounted while folded rather than merely hidden with
 * CSS: leaving it in the DOM is how a harness measures a panel nobody can
 * see, and how a screen reader reads content the page says is closed. Only
 * the empty container element stays, so `aria-controls` still resolves.
 * Data loading lives in the calling section, not in here, so a folded card
 * still knows its count.
 *
 * `testid` (both modes) lands on the outer `<Card>`, and exists because
 * #456/#459 converted four settings sections that ALREADY carried one —
 * `settings-health-card`, `send-feedback-card`, `usage-disclosure`,
 * `settings-text-size-card` — asserted by the journey suite and used to
 * scope queries INSIDE a card. Converting a section to this primitive must
 * not silently rename its handle: a test that stops finding a card fails
 * loudly, but one that asserted `toHaveCount(0)` starts passing for the
 * wrong reason. Distinct from `toggleTestid`, which names the disclosure
 * BUTTON.
 *
 * `appearance` (issue #975/#982, Settings A, step 6) — opt-in, default
 * `"shipped"`. `Settings.dc.html` (design-baseline-round-5) draws every
 * card frame at 14px padding, not this component's shipped 24px (`p-6`);
 * `appearance="r5"` changes that padding — same border, same radius
 * (`rounded-xl` already resolves to the drawn 12px). `SettingsScreen.jsx`
 * is the one caller that ever passes it, on the four folds it mounts
 * (Health, Notifications, Send feedback, What we measure); every other
 * caller — Identity & Vision included — passes nothing and gets the
 * byte-identical shipped padding/header. Journey suites and
 * `identityForm.test.js`'s own call-site table are unaffected: neither
 * reads padding, and no existing call site gained a new prop it did not
 * already have a value for.
 *
 * #989 round 2 (screen gap) — `appearance="r5"` ALSO changes the
 * collapsible header's own layout now: the shipped header's `flex-wrap` +
 * the heading's `grow basis-0 min-w-[60%]` rule (below) drops the
 * header-state badge and chevron to their own line once the title+desc
 * block needs more than 40% of the row — which "Health and emergency
 * contact" plus a "Partly filled" badge does, at 390px. Every drawn
 * header (`SettingsHealth.dc.html`'s `settings-health-toggle` and its
 * three siblings) puts title, badge and chevron on ONE row always — only
 * the DESCRIPTION drops to its own line below — so the r5 branch is a
 * different shape, not a tighter version of the same one: title
 * flex-grow, badge/chevron shrink-0, sharing one row with a `min-height`
 * floor; description on its own row underneath. This is stricter than the
 * shipped rule (it never wraps the badge away at all), so every r5 caller
 * — not just Health — benefits, not only the one this was measured on.
 *
 * `tapBox` (#1394, D221 G4) — opt-in, off by default, and only read under
 * `appearance="r5"`. A fold whose header is shorter than the 44px tap token
 * (the Player Profile fold has no description line, so its header is the
 * 40px icon tile) passes it to get a 44px header button that takes no room.
 */
export function SectionCard({
  icon,
  title,
  desc,
  children,
  collapsible = false,
  count = null,
  summary = null,
  defaultOpen = true,
  toggleTestid,
  testid,
  appearance = "shipped",
  usageItem,
  tapBox = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  // `usageItem` names a Settings fold in the usage vocabulary; only OPENING is counted.
  const toggle = () => {
    if (!open && usageItem) track("settings.fold_open", { item: usageItem });
    setOpen((v) => !v);
  };
  const panelId = useId();
  const contentPadding = appearance === "r5" ? "p-3.5" : "p-6";

  if (collapsible) assertSectionHeaderState({ title, count, summary });

  // #1135 (D246): under r5 the frame is the kit's surface, not the shipped
  // shadcn `Card` (the same swap #1236 made on the guide page). Every other
  // appearance keeps `<Card>` exactly as before.
  const Frame = appearance === "r5" ? KitFrame : ShippedFrame;

  if (!collapsible) {
    return (
      <Frame contentPadding={contentPadding} testid={testid}>
        <SectionHeading icon={icon} title={title} desc={desc} />
        {children}
      </Frame>
    );
  }

  const Icon = icon;

  return (
    <Frame contentPadding={contentPadding} testid={testid}>
      {appearance !== "r5" ? (
        /* Issue #528: `flex-wrap` + the heading's `grow basis-0
            min-w-[60%]` is the header's narrow-width layout rule. The
            header-state span below is `shrink-0` (a real number is never
            abbreviated to fit — #528's acceptance), so on one row every
            pixel it takes comes off the title/desc block; Martin's phone
            rendered "Career snapshot" one word per line beside "412 runs
            · 17 wkts". The heading's basis-0 + min-w floor means the row
            wraps instead: while the state fits beside at least 60% of
            the row's width of heading, it stays one row (a one-digit
            count never wraps at any measured viewport); once it doesn't,
            the state takes its own line under the title — full string
            intact, title at full width — with `ml-auto` keeping the
            chevron on the right edge either way. */
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={panelId}
          data-testid={toggleTestid}
          className="w-full text-left flex flex-wrap items-center justify-between gap-x-3 gap-y-2 min-w-0"
        >
          <SectionHeading icon={icon} title={title} desc={desc} className="grow basis-0 min-w-[60%]" />
          <span className="flex items-center gap-2 shrink-0 ml-auto">
            {count != null && (
              <span
                className="inline-flex items-center rounded-full bg-[#0B2E5915] px-2.5 py-0.5 text-xs font-bold text-[#0B2E59]"
                data-testid={toggleTestid ? `${toggleTestid}-count` : undefined}
              >
                {count}
                <span className="sr-only"> {count === 1 ? "entry" : "entries"}</span>
              </span>
            )}
            {summary != null && (
              <span
                className="inline-flex items-center rounded-full bg-[#0B2E5915] px-2.5 py-0.5 text-xs font-bold text-[#0B2E59]"
                data-testid={toggleTestid ? `${toggleTestid}-summary` : undefined}
              >
                {summary}
              </span>
            )}
            <ChevronDown
              size={18}
              className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </span>
        </button>
      ) : (
        // #989 round 2 (screen gap) — title, badge and chevron share ONE
        // row always (a `min-height` floor, never wrapping the badge/
        // chevron away); the description drops to its own row below,
        // exactly as every drawn header (`SettingsHealth.dc.html`'s
        // `settings-health-toggle` and its three siblings) draws it. A
        // SEPARATE button from the shipped one above, not a restyle of
        // it — the shipped branch's own markup (and the `HEADER` slice
        // `identityForm.test.js` reads from the FIRST `<button>` in
        // source) is unaffected by this branch's own existence.
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={panelId}
          data-testid={toggleTestid}
          className="w-full text-left flex items-start gap-3 min-w-0"
          // #1394 (D221 G4, D354): opt-in (`tapBox`), so a host that passes nothing keeps
          // main's exact markup. The header is then at least 44px to tap: the block padding
          // grows the box to the 44px tap token (the 40px icon tile, in rem, is its
          // shortest content), and the equal negative margin takes the growth back out of
          // the layout, so no card or fold moves and the content stays where it was drawn.
          style={tapBox ? R5_HEADER_TAP : undefined}
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
            <Icon size={18} color="#0B2E59" />
          </div>
          <div className="grow min-w-0 flex flex-col gap-0.5">
            <div className="flex items-center gap-2 min-h-[36px]">
              {/* F5 (#989 round 3, screen): the drawn 15px
                  (`SettingsHealth.dc.html`'s own `settings-health-toggle`
                  title, and its three siblings), not the shipped
                  `text-lg` (18px, rem) — at 18px "Health and emergency
                  contact" wraps onto three lines at 390. r5 branch only;
                  the shipped `SectionHeading` above keeps `text-lg`. */}
              <h3 className="grow min-w-0 font-display font-bold text-[15px] text-slate-900 break-words">{title}</h3>
              <span className="flex items-center gap-2 shrink-0">
                {count != null && (
                  <span
                    className="inline-flex items-center rounded-full bg-[#0B2E5915] px-2.5 py-0.5 text-xs font-bold text-[#0B2E59]"
                    data-testid={toggleTestid ? `${toggleTestid}-count` : undefined}
                  >
                    {count}
                    <span className="sr-only"> {count === 1 ? "entry" : "entries"}</span>
                  </span>
                )}
                {summary != null && (
                  <span
                    className="inline-flex items-center rounded-full bg-[#0B2E5915] px-2.5 py-0.5 text-xs font-bold text-[#0B2E59]"
                    data-testid={toggleTestid ? `${toggleTestid}-summary` : undefined}
                  >
                    {summary}
                  </span>
                )}
                <ChevronDown
                  size={18}
                  className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </span>
            </div>
            {desc && <p className="text-sm text-slate-500 break-words">{desc}</p>}
          </div>
        </button>
      )}
      {/* The container stays in the DOM so `aria-controls` always resolves
          to a real element; only the CONTENT unmounts. */}
      <div id={panelId} className={open ? "space-y-4" : "hidden"}>{open && children}</div>
    </Frame>
  );
}

export function Q({ children }) {
  return <Label className="text-sm font-semibold text-slate-800 block mt-2">{children}</Label>;
}

export function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export function SaveRow({ onSave, busy, testid }) {
  return (
    <div className="flex justify-end pt-2 border-t border-slate-100">
      <Button onClick={onSave} disabled={busy} data-testid={testid} style={{ background: "#0B2E59" }}>
        {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
        Save section
      </Button>
    </div>
  );
}
